/**
 * SkipCheckModal.js
 *
 * Big-button modal for selecting skip reason (mandatory for audit compliance).
 * Designed for glove operation with 56px minimum tap targets.
 */

import React, { useState } from 'react';
import { MinusCircle, X, Clock, XCircle, Wrench, MoreHorizontal } from 'lucide-react';

// Skip reason options with icons and colors
const SKIP_REASONS = [
  {
    value: 'deferred',
    label: 'Deferred',
    description: 'Will complete later',
    icon: Clock,
    color: 'amber'
  },
  {
    value: 'not_applicable',
    label: 'Not Needed',
    description: 'Not required for this voyage',
    icon: XCircle,
    color: 'slate'
  },
  {
    value: 'equipment_issue',
    label: 'Equip Issue',
    description: 'Equipment unavailable',
    icon: Wrench,
    color: 'red'
  },
  {
    value: 'other',
    label: 'Other',
    description: 'See notes below',
    icon: MoreHorizontal,
    color: 'slate'
  }
];

// Color utility
const getButtonColor = (colorName, isSelected) => {
  const colors = {
    amber: isSelected
      ? 'bg-amber-600 border-amber-700 ring-4 ring-amber-300 text-white'
      : 'bg-amber-100 border-amber-200 text-amber-700 hover:bg-amber-200',
    slate: isSelected
      ? 'bg-slate-700 border-slate-700 ring-4 ring-slate-300 text-white'
      : 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200',
    red: isSelected
      ? 'bg-red-600 border-red-700 ring-4 ring-red-300 text-white'
      : 'bg-red-100 border-red-200 text-red-700 hover:bg-red-200'
  };
  return colors[colorName] || colors.slate;
};

const SkipCheckModal = ({
  isOpen,
  checkText,
  check,  // V2 API - pass full check object
  onConfirm,
  onCancel
}) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [notes, setNotes] = useState('');

  // Support both APIs: legacy (isOpen + checkText) and V2 (check object)
  const displayText = checkText || check?.text || 'Unknown check';
  const shouldShow = isOpen || !!check;

  if (!shouldShow) return null;

  const handleConfirm = () => {
    if (!selectedReason) return;
    onConfirm(selectedReason, notes);
    setSelectedReason('');
    setNotes('');
  };

  const handleCancel = () => {
    setSelectedReason('');
    setNotes('');
    onCancel();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={handleCancel} />

      {/* Modal */}
      <div className="relative w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-amber-600">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <MinusCircle className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Skip Check</h3>
              <p className="text-sm text-amber-100">Select a reason</p>
            </div>
          </div>
          <button
            onClick={handleCancel}
            className="p-2 hover:bg-amber-700 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Check being skipped */}
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-sm text-slate-500 mb-1">Skipping:</p>
            <p className="text-base font-medium text-slate-900">"{displayText}"</p>
          </div>

          {/* Reason selection - 2x2 button grid */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-3">
              Reason (required)
            </label>
            <div className="grid grid-cols-2 gap-3">
              {SKIP_REASONS.map((reason) => {
                const Icon = reason.icon;
                const isSelected = selectedReason === reason.value;
                return (
                  <button
                    key={reason.value}
                    onClick={() => setSelectedReason(reason.value)}
                    className={`
                      min-h-[72px] p-3 rounded-lg border-2 transition-all
                      flex flex-col items-center justify-center gap-1
                      ${getButtonColor(reason.color, isSelected)}
                    `}
                  >
                    <Icon className={`h-5 w-5 ${isSelected ? 'text-white' : ''}`} />
                    <span className="text-sm font-bold">{reason.label}</span>
                    <span className={`text-xs ${isSelected ? 'text-white/80' : 'opacity-70'}`}>
                      {reason.description}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Notes {selectedReason === 'other' ? '(required for "Other")' : '(optional)'}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional details..."
              rows={2}
              className="w-full px-4 py-3 text-base border border-slate-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 p-4 border-t border-slate-200 bg-slate-50">
          <button
            onClick={handleCancel}
            className="flex-1 py-3 px-4 min-h-[56px] border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedReason || (selectedReason === 'other' && !notes.trim())}
            className="flex-1 py-3 px-4 min-h-[56px] bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Skip Check
          </button>
        </div>
      </div>
    </div>
  );
};

export default SkipCheckModal;
